import React, {useEffect, useReducer} from 'react';
import {DailyCareLogContext} from './context';
import reducer from './reducer';
import {DailyCareLogContextActionType, DailyCareLogContextState} from './types';
import {NotificationType} from '../../backend/enums/notification-type';
import {useAuth} from '../auth/context';
import {useEntity} from '../entity/context';

// Set Initial State
const initialState: DailyCareLogContextState = {
  navigationHeader: {
    active: false,
    text: '',
  },
  bottomNavigation: {
    visible: true,
    active: false,
  },
  occurrence: undefined,
};

export const DailyCareLogContextProvider = (props: {children: React.ReactNode}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {updateEntityOccurrencesCount} = useEntity();
  const {
    state: {session},
  } = useAuth();
  const selectedEntityId = session?.selectedAffiliation?.entity.id;

  useEffect(() => {
    // On service worker message
    const onServiceWorkerMessage = (event: MessageEvent) => {
      const messageData = event.data;
      const notificationData = messageData?.data ?? messageData?.firebaseSWData;
      const notificationType = notificationData?.notificationType;
      const notificationEntityId = notificationData?.entityId;

      console.debug('📩 Service worker message in daily care log:', messageData);

      if (
        notificationType === NotificationType.NewOccurrence ||
        notificationType === NotificationType.NewOccurrenceAssigned
      ) {
        // Legacy service workers did not include entityId in this message. In that case we still
        // refresh the count; newer service workers include it so we can ignore other entities.
        if (!notificationEntityId || notificationEntityId === selectedEntityId) {
          console.debug('🔄 Refreshing occurrences count in daily care log');
          updateEntityOccurrencesCount();
        } else {
          console.debug('⏹️ Occurrence notification is for another entity.');
        }
      } else {
        console.debug('⏹️ Service worker message not handled in daily care log.');
      }
    };

    // Listen
    navigator.serviceWorker?.addEventListener('message', onServiceWorkerMessage);

    return () => {
      // Unsubscribe
      navigator.serviceWorker?.removeEventListener('message', onServiceWorkerMessage);
    };
  }, [selectedEntityId, updateEntityOccurrencesCount]);

  const setNavigationHeader = async (active: boolean, text: string): Promise<any> => {
    dispatch({
      type: DailyCareLogContextActionType.SET_NAVIGATION_HEADER,
      active,
      text,
    });
  };

  const toggleBottomNavigation = async (visible: boolean, active?: boolean): Promise<any> => {
    dispatch({
      type: DailyCareLogContextActionType.TOGGLE_BOTTOM_NAVIGATION,
      visible,
      active: !!active,
    });
  };

  return (
    <DailyCareLogContext.Provider
      value={{
        state,
        setNavigationHeader,
        toggleBottomNavigation,
      }}
    >
      {props.children}
    </DailyCareLogContext.Provider>
  );
};
